import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { onAuthStateChanged, type User } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";

type Role = "admin" | "editor" | "writer";

interface AdminGuardState {
  status: "loading" | "anon" | "authorised" | "unauthorised";
  user?: User;
  role?: Role;
  contributorSlug?: string;
  error?: string;
}

interface AdminGuardProps {
  children: React.ReactNode;
  /** Subset of roles that can view this surface. Default: all three. */
  allow?: Role[];
}

export function AdminGuard({
  children,
  allow = ["admin", "editor", "writer"],
}: AdminGuardProps) {
  const location = useLocation();
  const [state, setState] = React.useState<AdminGuardState>({ status: "loading" });

  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setState({ status: "anon" });
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (!snap.exists()) {
          // First sign-in — bridge endpoint provisions users/{uid} from
          // contributors lookup. Call it then re-check.
          const idToken = await user.getIdToken();
          const res = await fetch("/api/admin/voices/bridge-session", {
            method: "POST",
            headers: { Authorization: `Bearer ${idToken}` },
          });
          if (!res.ok) {
            const payload = await res.json().catch(() => ({}));
            setState({
              status: "unauthorised",
              user,
              error: typeof payload.error === "string"
                ? payload.error
                : `Bridge failed: ${res.status}`,
            });
            return;
          }
          const data = (await res.json()) as {
            role: Role;
            contributorSlug?: string;
          };
          if (!allow.includes(data.role)) {
            setState({
              status: "unauthorised",
              user,
              error: `Role '${data.role}' is not permitted on this page.`,
            });
            return;
          }
          setState({
            status: "authorised",
            user,
            role: data.role,
            contributorSlug: data.contributorSlug,
          });
          return;
        }
        const data = snap.data() as { role?: Role; contributorSlug?: string };
        const role = data?.role;
        if (!role || !allow.includes(role)) {
          setState({
            status: "unauthorised",
            user,
            error: `Role '${role ?? "none"}' is not permitted on this page.`,
          });
          return;
        }
        setState({
          status: "authorised",
          user,
          role,
          contributorSlug: data.contributorSlug,
        });
      } catch (err) {
        setState({
          status: "unauthorised",
          user,
          error: `Could not verify role: ${String(err)}`,
        });
      }
    });
    return unsub;
  }, [allow]);

  if (state.status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <p className="font-mono text-xs text-ink-3 uppercase tracking-widest">
          Verifying…
        </p>
      </div>
    );
  }

  if (state.status === "anon") {
    return (
      <Navigate
        to={`/admin/sign-in?redirect=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  if (state.status === "unauthorised") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper px-6">
        <div className="max-w-md text-center">
          <p className="font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-3">
            Access denied
          </p>
          <h1 className="font-serif text-2xl text-ink mb-4">Not authorised</h1>
          <p className="font-serif text-sm text-ink-2 mb-6 leading-relaxed">
            {state.error ?? "Your account does not have access to this page."}
          </p>
          <p className="font-mono text-xs text-ink-3">
            Signed in as {state.user?.email ?? "unknown"}.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
